import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { copyToClipboard, formatDateTime } from '/imports/utils';
import { useMethod, useUser } from '/imports/hooks';
import {
  deleteTestRun, getTestResultsKeys, getTestRun, getTestRunsSummary, listTestRuns, TestRun, testRunEndpoints, TestRunKind,
  TestRunKindSummary, testRunKinds, TestRunResult,
} from '/imports/api/testRuns';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Icon } from '/imports/ui/Icon';
import { Button } from '/imports/ui/Button';
import { Tabs } from '/imports/ui/Tabs';
import { ListMethodCatalog } from '/imports/ui/ListMethodCatalog';
import { Column } from '/imports/ui/DataTable';
import { FileViewerModal } from '/imports/ui/FileViewerModal';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { Modal, ModalStyled } from '/imports/ui/Modal';
import { LoadingFill } from '/imports/ui/LoadingFill';
import { toastOnError } from '/imports/ui/utils';

type Verdict = 'passed' | 'failed' | 'empty';

const kindLabel = (kind: TestRunKind) => (kind === 'manual' ? strings.ManualTests() : strings.AutomatedTests());

const ApiKeysModalStyled = styled(ModalStyled)`
  .modal-window { width: 38rem; max-width: 100%; }
  .hint { color: ${css.grey3}; font-size: 0.85rem; margin: 0 0 1rem; }
  .key { border-top: 1px solid ${css.grey6}; padding: 0.8rem 0; }
  .key:last-child { border-bottom: 1px solid ${css.grey6}; }
  .key .head { display: flex; align-items: baseline; gap: 0.6rem; margin-bottom: 0.45rem; }
  .key .head code { color: ${css.grey3}; font-size: 0.8rem; }
  .key .value { display: flex; align-items: center; gap: 0.5rem; }
  /* The key is long and must stay readable in one piece rather than wrap mid-token. */
  .key .value code { flex: 1; overflow-x: auto; white-space: nowrap; font-size: 0.8rem; background: ${css.grey7}; padding: 0.3rem 0.45rem; border-radius: 0.25rem; }
  .key .muted { color: ${css.grey4}; font-size: 0.85rem; }
`;

/**
 * The keys QA posts with. Fetched when this opens rather than with the page, and each one stays hidden
 * until asked for: the page is often the one on screen when somebody is sharing it.
 */
const ApiKeysModal = ({ onClose }: { onClose: () => void }) => {
  const [keys, loading] = useMethod(getTestResultsKeys, undefined, []);
  const [shown, setShown] = useState<TestRunKind[]>([]);
  const copy = (key: string) => {
    copyToClipboard(key);
    toast.success(strings.CopiedToClipboard());
  };
  return (
    <Modal styled={ApiKeysModalStyled} title={strings.ApiKeys()} onClose={onClose}>
      <p className="hint">{strings.TestResultsKeysHint()}</p>
      {loading && !keys ? <LoadingFill /> : testRunKinds.map((kind) => {
        const key = keys?.[kind];
        return (
          <div className="key" key={kind}>
            <div className="head">
              <b>{kindLabel(kind)}</b>
              <code>POST {testRunEndpoints[kind]}</code>
            </div>
            {!key && <div className="muted">{strings.KeyNotConfigured()}</div>}
            {!!key && !shown.includes(kind) &&
              <Button className="secondary compact" onClick={() => setShown((kinds) => [...kinds, kind])}>{strings.Show()}</Button>}
            {!!key && shown.includes(kind) &&
              <div className="value">
                <code>{key}</code>
                <Button className="secondary compact" onClick={() => copy(key)}>{strings.Copy()}</Button>
                <Button className="secondary compact" onClick={() => setShown((kinds) => kinds.filter((k) => k !== kind))}>{strings.Hide()}</Button>
              </div>}
          </div>
        );
      })}
    </Modal>
  );
};

const verdictOf = (totals: { passed: number, failed: number }): Verdict => {
  if (totals.failed > 0) return 'failed';
  return totals.passed > 0 ? 'passed' : 'empty';
};

const verdictLabel = (verdict: Verdict) => {
  if (verdict === 'failed') return strings.Failed();
  if (verdict === 'passed') return strings.Passed();
  return strings.Skipped();
};

const formatPassRate = (rate?: number) => {
  if (rate === undefined) return '—';
  return `${Number.isInteger(rate) ? rate : rate.toFixed(1)}%`;
};

const formatRunDuration = (ms?: number) => {
  if (ms === undefined) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const relativeTime = (date?: Date) => (date ? DateTime.fromJSDate(date).toRelative() || '' : '');

const hostOf = (url?: string) => {
  if (!url) return '';
  try {
    return new URL(url).host;
  }
  catch {
    return url;
  }
};

// Shared by the summary cards, the runs table and the results modal, so a verdict looks the same everywhere.
const pillCss = `
  .pill {
    display: inline-block; border-radius: 1rem; padding: 0.1rem 0.6rem; font-size: 0.72rem; font-weight: 600;
    letter-spacing: 0.04em; text-transform: uppercase; color: ${css.white}; background: ${css.grey4}; white-space: nowrap;
    &.passed { background: ${css.green2}; }
    &.failed { background: ${css.red2}; }
    &.skipped, &.empty { background: ${css.grey4}; }
  }
`;

const TestResultsPageStyled = styled(Page)`
  ${pillCss}
  .page-body { display: flex; flex-direction: column; gap: 1rem; padding-bottom: 1rem; }
  .summaries { display: grid; grid-template-columns: repeat(auto-fit, minmax(22rem, 1fr)); gap: 1rem; }
  .summary-card {
    background: ${css.white}; border: 1px solid ${css.grey7}; border-radius: ${css.inputBorderRadius}; padding: 1rem 1.25rem;
    border-top: 4px solid ${css.grey6};
    &.passed { border-top-color: ${css.green2}; }
    &.failed { border-top-color: ${css.red2}; }
    header { display: flex; align-items: baseline; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 0.75rem; }
    h2 { margin: 0; font-size: 1.1rem; }
    .ago { color: ${css.grey4}; font-size: 0.85rem; }
    .ago .link-btn { font-size: inherit; }
  }
  .tiles { display: grid; grid-template-columns: repeat(auto-fit, minmax(9.5rem, 1fr)); gap: 0.75rem; }
  .tile {
    background: ${css.grey9}; border-radius: ${css.inputBorderRadius}; padding: 0.6rem 0.75rem; min-width: 0;
    .tile-label { font-size: 0.68rem; letter-spacing: 0.05em; text-transform: uppercase; color: ${css.grey4}; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .tile-value { font-size: 1.6rem; font-weight: 600; line-height: 1.2; font-variant-numeric: tabular-nums; margin-top: 0.15rem; }
    .tile-sub { font-size: 0.78rem; color: ${css.grey3}; margin-top: 0.1rem; overflow-wrap: anywhere; }
    &.passed .tile-value { color: ${css.green2}; }
    &.failed .tile-value { color: ${css.red2}; }
  }
  .card-section { margin-top: 0.9rem; }
  .card-section h3 { margin: 0 0 0.35rem; font-size: 0.78rem; letter-spacing: 0.05em; text-transform: uppercase; color: ${css.grey4}; font-weight: 600; }
  .card-section ul { margin: 0; padding-left: 1.1rem; font-size: 0.85rem; }
  .card-section li { overflow-wrap: anywhere; }
  .offenders { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  .offenders th { text-align: left; font-weight: 500; color: ${css.grey4}; padding: 0.15rem 0.5rem 0.15rem 0; }
  .offenders td { padding: 0.15rem 0.5rem 0.15rem 0; overflow-wrap: anywhere; }
  .offenders td.num, .offenders th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .runs-head { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; }
  .runs-head .tabs { flex: 0 0 auto; }
  .runs { flex: 1; min-height: 24rem; display: flex; flex-direction: column; }
  .runs .catalog { flex: 1; }
  .run-row {
    cursor: pointer;
    &:hover td { background: ${css.grey9}; }
    td { padding: 0.5rem 0.75rem; border-bottom: 1px solid ${css.grey8}; vertical-align: middle; white-space: nowrap; }
    td.num { text-align: right; font-variant-numeric: tabular-nums; }
    td.wrap { white-space: normal; overflow-wrap: anywhere; }
    .ago { display: block; color: ${css.grey4}; font-size: 0.78rem; }
    .muted { color: ${css.grey4}; }
    .mono { font-family: monospace; font-size: 0.85rem; }
    .rate { margin-left: 0.5rem; font-variant-numeric: tabular-nums; }
    .row-actions { display: flex; align-items: center; gap: 0.75rem; justify-content: flex-end; }
  }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font: inherit; font-size: 0.9rem; }
  .link-btn:hover { text-decoration: underline; }
`;

const ResultsModalStyled = styled(ModalStyled)`
  ${pillCss}
  .modal-window { width: min(70rem, 95vw); max-width: 100%; }
  .meta { color: ${css.grey4}; font-size: 0.85rem; display: flex; flex-wrap: wrap; gap: 0.25rem 1rem; margin: 0.25rem 0 0.75rem; }
  .toolbar { display: flex; align-items: center; gap: 1rem; margin-bottom: 0.75rem; flex-wrap: wrap; }
  .toolbar .counts { display: flex; gap: 0.75rem; font-variant-numeric: tabular-nums; font-size: 0.9rem; }
  .toolbar .spacer { flex: 1; }
  .link-btn { background: none; border: none; color: ${css.primaryColor}; cursor: pointer; padding: 0; font: inherit; font-size: 0.9rem; }
  .results-wrap { position: relative; max-height: 60vh; overflow: auto; min-height: 8rem; }
  table.results { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
  table.results th { position: sticky; top: 0; background: ${css.grey9}; color: ${css.grey4}; font-weight: 500; text-align: left; padding: 0.4rem 0.6rem; }
  table.results td { padding: 0.4rem 0.6rem; border-bottom: 1px solid ${css.grey8}; vertical-align: top; }
  table.results td.name { overflow-wrap: anywhere; }
  table.results td.suite { color: ${css.grey4}; font-size: 0.8rem; white-space: nowrap; }
  table.results td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  table.results tr.message td { border-bottom: 1px solid ${css.grey7}; padding-top: 0; }
  table.results pre { margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 0.8rem; color: ${css.grey3}; background: ${css.grey9}; padding: 0.5rem 0.75rem; border-left: 3px solid ${css.red2}; }
  .tag { display: inline-block; margin-left: 0.4rem; font-size: 0.7rem; color: ${css.grey3}; border: 1px solid ${css.grey6}; border-radius: 0.5rem; padding: 0 0.35rem; }
  .empty { padding: 2rem 1rem; text-align: center; color: ${css.grey4}; }
`;

interface TileProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  tone?: Verdict | 'none';
}

const Tile = ({ label, value, sub, tone }: TileProps) => (
  <div className={`tile ${tone && tone !== 'none' && tone !== 'empty' ? tone : ''}`}>
    <div className="tile-label">{label}</div>
    <div className="tile-value">{value}</div>
    {sub ? <div className="tile-sub">{sub}</div> : null}
  </div>
);

const NEW_FAILURES_SHOWN = 6;

interface KindSummaryCardProps {
  summary: TestRunKindSummary;
  onOpenLatest(): void;
}

/** One kind's headline metrics: the latest verdict, the week's trend, volume, what is failing and for how long. */
const KindSummaryCard = ({ summary, onOpenLatest }: KindSummaryCardProps) => {
  const { latest } = summary;
  const verdict = latest ? verdictOf(latest.totals) : undefined;
  const executed = latest ? latest.totals.passed + latest.totals.failed : 0;
  const delta = summary.avgPassRate7d !== undefined && summary.avgPassRatePrior7d !== undefined
    ? Math.round((summary.avgPassRate7d - summary.avgPassRatePrior7d) * 10) / 10
    : undefined;
  const trendSub = delta !== undefined
    ? `${delta > 0 ? '+' : ''}${delta} ${strings.Points()} ${strings.VsPriorWeek()}`
    : summary.avgPassRate30d !== undefined ? `${formatPassRate(summary.avgPassRate30d)} · ${strings.Last30Days()}` : undefined;
  const trendTone: TileProps['tone'] = delta === undefined || delta === 0 ? 'none' : delta > 0 ? 'passed' : 'failed';
  const failingSub = latest
    ? `${summary.newFailures.length} ${strings.FailuresNew()} · ${summary.persistingFailures.length} ${strings.FailuresPersisting()} · ${summary.resolvedFailures.length} ${strings.FailuresResolved()}`
    : undefined;
  return (
    <section className={`summary-card ${verdict || ''}`}>
      <header>
        <h2>{kindLabel(summary.kind)}</h2>
        {latest && verdict ? (
          <>
            <span className={`pill ${verdict}`}>{verdictLabel(verdict)}</span>
            <span className="ago">
              <button type="button" className="link-btn" onClick={onOpenLatest}>{strings.LatestRun()}</button> · {relativeTime(latest.startedAt)}
              {latest.durationMs !== undefined ? ` · ${formatRunDuration(latest.durationMs)}` : ''}
            </span>
          </>
        ) : <span className="ago">{strings.NoTestRunsYet()}</span>}
      </header>
      <div className="tiles">
        <Tile
          label={strings.PassRate()}
          value={latest ? formatPassRate(latest.passRate) : '—'}
          sub={latest ? `${latest.totals.passed} / ${executed} ${strings.Passed()}${summary.previous ? ` · ${strings.Previous()} ${formatPassRate(summary.previous.passRate)}` : ''}` : undefined}
          tone={verdict}
        />
        <Tile label={strings.SevenDayAverage()} value={formatPassRate(summary.avgPassRate7d)} sub={trendSub} tone={trendTone} />
        <Tile label={`${strings.Runs()} · ${strings.Last7Days()}`} value={summary.runs7d} sub={`${summary.runs30d} · ${strings.Last30Days()}`} />
        <Tile label={`${strings.TestsRun()} · ${strings.Last7Days()}`} value={summary.testsRun7d} sub={`${summary.testsRun30d} · ${strings.Last30Days()}`} />
        <Tile label={strings.FailingNow()} value={latest ? latest.totals.failed : '—'} sub={failingSub} tone={latest ? (latest.totals.failed ? 'failed' : 'passed') : 'none'} />
        <Tile label={strings.GreenStreak()} value={summary.greenStreak} sub={strings.ConsecutivePassingRuns()} tone={summary.greenStreak ? 'passed' : 'none'} />
      </div>
      {summary.newFailures.length > 0 && (
        <div className="card-section">
          <h3>{strings.NewFailures()}</h3>
          <ul>
            {summary.newFailures.slice(0, NEW_FAILURES_SHOWN).map((name) => <li key={name}>{name}</li>)}
            {summary.newFailures.length > NEW_FAILURES_SHOWN && <li className="muted">+{summary.newFailures.length - NEW_FAILURES_SHOWN}</li>}
          </ul>
        </div>
      )}
      {summary.repeatOffenders.length > 0 && (
        <div className="card-section">
          <h3>{strings.MostFrequentFailures()}</h3>
          <table className="offenders">
            <thead><tr><th>{strings.Name()}</th><th className="num">{strings.Failed()}</th><th className="num">{strings.Runs()}</th></tr></thead>
            <tbody>
              {summary.repeatOffenders.map((offender) => (
                <tr key={offender.name}><td>{offender.name}</td><td className="num">{offender.failures}</td><td className="num">{offender.runs}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const outcomeRank: { [status in TestRunResult['status']]: number } = { 'failed': 0, 'skipped': 1, 'passed': 2 };

interface TestRunResultsModalProps {
  run: TestRun;
  onClose(): void;
  onOpenReport(): void;
}

/** Every test in one run, failures first with their messages; passes are folded away until asked for. */
const TestRunResultsModal = ({ run, onClose, onOpenReport }: TestRunResultsModalProps) => {
  const [full, loading] = useMethod(getTestRun, run._id, [run._id]);
  const [showPassed, setShowPassed] = useState(false);
  const results = useMemo(() => {
    const all = (full?.results || []).map((result, index) => ({ result, index }));
    all.sort((a, b) => outcomeRank[a.result.status] - outcomeRank[b.result.status] || a.index - b.index);
    return all.map((entry) => entry.result);
  }, [full]);
  const shown = showPassed ? results : results.filter((result) => result.status !== 'passed');
  const verdict = verdictOf(run.totals);
  return (
    <Modal styled={ResultsModalStyled} title={`${strings.Tests()} · ${formatDateTime(run.startedAt)}`} onClose={onClose}>
      <div className="meta">
        {run.label && <span>{run.label}</span>}
        {run.targetUrl && <span>{strings.Target()}: {hostOf(run.targetUrl)}</span>}
        {run.gitSha && <span>{strings.Commit()}: {run.gitSha.slice(0, 10)}</span>}
        {run.host && <span>{strings.Host()}: {run.host}</span>}
        {run.platform && <span>{strings.Platform()}: {run.platform}</span>}
        {run.durationMs !== undefined && <span>{strings.Duration()}: {formatRunDuration(run.durationMs)}</span>}
        <span>{strings.Received()}: {formatDateTime(run.receivedAt)}</span>
      </div>
      <div className="toolbar">
        <span className={`pill ${verdict}`}>{verdictLabel(verdict)}</span>
        <span className="counts">
          <span>{run.totals.passed} {strings.Passed()}</span>
          <span>{run.totals.failed} {strings.Failed()}</span>
          <span>{run.totals.skipped} {strings.Skipped()}</span>
          <span>{formatPassRate(run.passRate)}</span>
        </span>
        <span className="spacer" />
        <button type="button" className="link-btn" onClick={() => setShowPassed(!showPassed)}>{showPassed ? strings.HidePassedTests() : strings.ShowPassedTests()}</button>
        {run.pdf ? <button type="button" className="link-btn" onClick={onOpenReport}>{strings.OpenReport()}</button> : <span className="muted">{strings.NoReportAttached()}</span>}
      </div>
      <div className="results-wrap">
        {loading ? <LoadingFill /> : shown.length ? (
          <table className="results">
            <thead>
              <tr><th>{strings.Status()}</th><th>{strings.Suite()}</th><th>{strings.Name()}</th><th className="num">{strings.Duration()}</th></tr>
            </thead>
            <tbody>
              {shown.map((result, index) => (
                <React.Fragment key={`${result.name}-${index}`}>
                  <tr>
                    <td><span className={`pill ${result.status}`}>{result.status === 'failed' ? strings.Failed() : result.status === 'passed' ? strings.Passed() : strings.Skipped()}</span></td>
                    <td className="suite">{result.suite || ''}</td>
                    <td className="name">
                      {result.name}
                      {result.tags?.map((tag) => <span key={tag} className="tag">{tag}</span>)}
                    </td>
                    <td className="num">{result.durationMs !== undefined ? formatRunDuration(result.durationMs) : ''}</td>
                  </tr>
                  {result.message && (
                    <tr className="message"><td colSpan={4}><pre>{result.message}</pre></td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        ) : <div className="empty">{strings.NoResults()}</div>}
      </div>
    </Modal>
  );
};

const columns: Column<TestRun>[] = [
  { 'field': 'startedAt', 'label': strings.Started },
  { 'field': 'label', 'label': strings.Run },
  { 'field': 'passRate', 'label': strings.Status },
  { 'field': 'totals.passed' as keyof TestRun, 'label': strings.Passed },
  { 'field': 'totals.failed' as keyof TestRun, 'label': strings.Failed },
  { 'field': 'totals.skipped' as keyof TestRun, 'label': strings.Skipped },
  { 'field': 'durationMs', 'label': strings.Duration },
  { 'field': 'targetUrl', 'label': strings.Target },
  { 'field': 'gitSha', 'label': strings.Commit },
  { 'field': 'pdf.url' as keyof TestRun, 'label': strings.Report },
];

/**
 * Super-user page for the QA engineers' posted results: headline metrics per kind of run at the top, then the
 * runs themselves, one table per kind. A row opens the run's report PDF; the Tests link opens its per-test
 * results.
 */
export const TestResultsPage = () => {
  const user = useUser();
  const [params, setParams] = useSearchParams();
  const kind: TestRunKind = params.get('kind') === 'manual' ? 'manual' : 'automated';
  const [refreshKey, setRefreshKey] = useState(0);
  const [summary, loadingSummary] = useMethod(getTestRunsSummary, undefined, [refreshKey]);
  const [viewing, setViewing] = useState<TestRun>();
  const [inspecting, setInspecting] = useState<TestRun>();
  const [deleting, setDeleting] = useState<TestRun>();
  const [showingKeys, setShowingKeys] = useState(false);
  if (user && !user.isSuperUser) return <NotPermittedPage />;

  const refresh = () => setRefreshKey((key) => key + 1);
  const openRun = (run: TestRun) => {
    if (run.pdf) setViewing(run);
    else setInspecting(run);
  };
  const openLatest = toastOnError(async (_id: string) => {
    const run = await getTestRun.call(_id);
    openRun(run);
  });
  const confirmDelete = toastOnError(async () => {
    if (!deleting) return;
    await deleteTestRun.call(deleting._id);
    setDeleting(undefined);
    toast.success(strings.TestRunDeleted());
    refresh();
  });

  return (
    <TestResultsPageStyled className="page test-results-page">
      <PageHeading
        left={<h1><Icon icon="circleCheck" />{strings.TestResults()}</h1>}
        right={
          <>
            <Button className="secondary" onClick={() => setShowingKeys(true)}>{strings.ApiKeys()}</Button>
            <Button className="secondary" onClick={refresh}>{strings.Refresh()}</Button>
          </>
        }
      />
      <PageBody>
        <div className="summaries">
          {loadingSummary && !summary ? <LoadingFill /> : testRunKinds.map((k) => (
            summary?.[k] ? <KindSummaryCard key={k} summary={summary[k]} onOpenLatest={() => summary[k].latest && openLatest(summary[k].latest._id)} /> : null
          ))}
        </div>
        <div className="runs-head">
          <Tabs tabs={testRunKinds.map((k) => ({ 'key': k, 'label': kindLabel(k), 'active': k === kind, 'onClick': () => setParams({ kind: k }) }))} tabWidth="11rem" />
        </div>
        <div className="runs">
          <ListMethodCatalog<TestRun>
            key={`${kind}-${refreshKey}`}
            listMethod={listTestRuns}
            columns={columns}
            query={{ kind }}
            defaultSort={{ startedAt: -1 }}
            limit={50}
            row={(run) => {
              const verdict = verdictOf(run.totals);
              return (
                <tr key={run._id} className={`run-row ${verdict}`} onClick={() => openRun(run)}>
                  <td className="row-select" />
                  <td>{formatDateTime(run.startedAt)}<span className="ago">{relativeTime(run.startedAt)}</span></td>
                  <td className="wrap">{run.label || <span className="muted">—</span>}</td>
                  <td><span className={`pill ${verdict}`}>{verdictLabel(verdict)}</span><span className="rate">{formatPassRate(run.passRate)}</span></td>
                  <td className="num">{run.totals.passed}</td>
                  <td className="num">{run.totals.failed}</td>
                  <td className="num">{run.totals.skipped}</td>
                  <td className="num">{formatRunDuration(run.durationMs)}</td>
                  <td>{hostOf(run.targetUrl) || <span className="muted">—</span>}</td>
                  <td className="mono">{run.gitSha ? run.gitSha.slice(0, 10) : <span className="muted">—</span>}</td>
                  <td>{run.pdf ? <Icon icon="filePresent" tip={strings.OpenReport()} /> : <span className="muted">—</span>}</td>
                  <td className="row-actions" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="link-btn" onClick={() => setInspecting(run)}>{strings.Tests()}</button>
                    <Icon icon="cancel" tip={strings.Delete()} onClick={() => setDeleting(run)} />
                  </td>
                </tr>
              );
            }}
          />
        </div>
      </PageBody>
      {viewing?.pdf && (
        <FileViewerModal file={viewing.pdf} title={`${kindLabel(viewing.kind)} · ${formatDateTime(viewing.startedAt)}`} downloadName={viewing.pdf.name} onClose={() => setViewing(undefined)} />
      )}
      {inspecting && (
        <TestRunResultsModal run={inspecting} onClose={() => setInspecting(undefined)} onOpenReport={() => { setViewing(inspecting); setInspecting(undefined); }} />
      )}
      {deleting && (
        <ConfirmModal title={strings.Delete()} body={strings.DeleteTestRunConfirm()} onClose={() => setDeleting(undefined)} onConfirm={confirmDelete} />
      )}
      {showingKeys && <ApiKeysModal onClose={() => setShowingKeys(false)} />}
    </TestResultsPageStyled>
  );
};
